/**
 * Taxation (admin → Billing) — withholding-tax records on invoices.
 *
 * Search a customer that has invoices → see their issued invoices, current
 * PortaBilling balance, and their tax-withheld records. A billing officer adds a
 * record (invoice + amount + date + certificate); a finance manager approves it,
 * then posts it to PortaBilling as a one-time, irreversible manual credit (which
 * resyncs the balance).
 */
import { escapeHtml } from '../../src/lib/format.js';
import { hasRole } from '../../src/lib/roles.js';
import { withButtonBusy } from '../../src/lib/button-busy.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const flash = (m, t) => notify.showFlash(m, t);
const FINANCE = ['root', 'finance_manager'];
const EDIT_ROLES = ['root', 'finance_manager', 'sales_officer', 'sales_supervisor', 'billing_officer', 'billing_supervisor'];
const STATUS_CLASS = { pending: 'warn', approved: '', posted: 'ok', rejected: 'muted' };

let selected = null;          // { porta_customer_id, customer_name }
let detail = null;            // last-loaded detail payload
let searchTimer = null;

function money(cur, v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return `${cur || 'GMD'} ${Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v}`;
}

export async function mountTaxation(container) {
  container = container || document.getElementById('invoicingTaxationView');
  if (!container) return;
  selected = null;
  detail = null;
  container.innerHTML = `
    <div class="crm-detail-header" style="margin-bottom:10px;">
      <strong style="font-size:15px;">Taxation</strong>
      <span class="crm-detail-sub" style="margin-left:8px;">Record tax withheld on invoices, approve, and post the credit to billing</span>
    </div>
    <div class="field" style="min-width:320px; max-width:460px; position:relative;">
      <span class="crm-detail-sub">Customer (with invoices)</span>
      <input data-tax-search class="input" type="text" autocomplete="off" placeholder="Search by name or Porta id…">
      <div data-tax-results class="crm-inbox-table" style="display:none; position:absolute; z-index:20; top:100%; left:0; right:0; background:#fff; border:1px solid #e2e2e2; border-radius:8px; max-height:280px; overflow:auto; box-shadow:0 6px 20px rgba(0,0,0,.12);"></div>
    </div>
    <div data-tax-body style="margin-top:12px;"><div class="crm-detail-empty">Search for a customer to begin.</div></div>`;

  wireSearch(container);
  container.querySelector('[data-tax-body]').addEventListener('click', (ev) => onBodyClick(ev, container));
  container.querySelector('[data-tax-body]').addEventListener('submit', (ev) => onAddSubmit(ev, container));
}

function wireSearch(container) {
  const input = container.querySelector('[data-tax-search]');
  const results = container.querySelector('[data-tax-results]');
  input.addEventListener('input', () => {
    const q = input.value.trim();
    clearTimeout(searchTimer);
    if (q.length < 2) { results.style.display = 'none'; results.innerHTML = ''; return; }
    searchTimer = setTimeout(() => runSearch(container, q), 250);
  });
  results.addEventListener('click', (ev) => {
    const row = ev.target.closest('[data-pid]');
    if (!row) return;
    selected = { porta_customer_id: Number(row.dataset.pid), customer_name: row.dataset.name };
    input.value = row.dataset.name || `Customer ${row.dataset.pid}`;
    results.style.display = 'none';
    loadDetail(container);
  });
  document.addEventListener('click', (ev) => { if (!container.contains(ev.target)) results.style.display = 'none'; });
}

async function runSearch(container, q) {
  const results = container.querySelector('[data-tax-results]');
  results.style.display = '';
  results.innerHTML = '<div class="crm-detail-loading" style="padding:8px;">Searching…</div>';
  try {
    const data = await api.fetch(API.invoicingTaxCustomers(`q=${encodeURIComponent(q)}`), { method: 'GET' });
    const items = data.items || [];
    if (!items.length) { results.innerHTML = '<div class="crm-detail-empty" style="padding:8px;">No customers with invoices match.</div>'; return; }
    results.innerHTML = items.map((c) => `
      <div data-pid="${escapeHtml(String(c.porta_customer_id))}" data-name="${escapeHtml(c.customer_name || '')}"
        style="padding:8px 10px; cursor:pointer; border-bottom:1px solid #f0f0f0;">
        <div><strong>${escapeHtml(c.customer_name || ('Customer ' + c.porta_customer_id))}</strong></div>
        <div class="crm-detail-sub">#${escapeHtml(String(c.porta_customer_id))} · ${c.invoice_count} invoice(s)</div></div>`).join('');
  } catch (err) {
    results.innerHTML = '<div class="crm-detail-error" style="padding:8px;">Search unavailable.</div>';
  }
}

async function loadDetail(container) {
  const body = container.querySelector('[data-tax-body]');
  if (!selected) return;
  body.innerHTML = '<div class="crm-detail-loading">Loading…</div>';
  try {
    detail = await api.fetch(API.invoicingTaxCustomerDetail(selected.porta_customer_id), { method: 'GET' });
    renderDetail(body);
  } catch (err) {
    body.innerHTML = '<div class="crm-detail-error">Failed to load. Your role may not have invoicing access.</div>';
  }
}

function renderDetail(body) {
  const invoices = detail.invoices || [];
  const whs = detail.withholdings || [];
  const canEdit = hasRole(...EDIT_ROLES);
  const invoiceOptions = invoices.map((i) =>
    `<option value="${escapeHtml(i.id)}">${escapeHtml(i.number)} · ${money(i.currency, i.total)}</option>`).join('');

  const linkWarn = detail.linked === false
    ? '<span class="badge danger" title="This id does not resolve to a PortaBilling customer">Not linked to PortaBilling</span>'
    : '';
  const header = `<div class="crm-detail-header" style="margin-bottom:8px;">
      <strong>${escapeHtml(selected.customer_name || ('Customer ' + selected.porta_customer_id))}</strong>
      <span class="crm-detail-sub" style="margin-left:8px;">#${escapeHtml(String(selected.porta_customer_id))}</span>
      <span style="margin-left:auto; display:flex; gap:6px; align-items:center;">${linkWarn}
        <span class="badge">Balance: ${money(detail.currency, detail.balance)}</span></span>
    </div>
    ${detail.linked === false ? '<div class="crm-detail-empty" style="color:var(--danger,#b91c1c);">This customer isn’t linked to a PortaBilling account. Records can be added, but they can’t be posted to billing until the customer is linked.</div>' : ''}`;

  const invoiceList = `<div style="flex:1 1 320px; min-width:300px;">
      <div class="crm-detail-sub" style="font-weight:600; text-transform:uppercase; letter-spacing:.5px; margin-bottom:4px;">Invoices issued</div>
      ${invoices.length ? `<div class="table-wrap"><table class="crm-inbox-table">
        <thead><tr><th>Invoice</th><th>Total</th><th>Status</th></tr></thead>
        <tbody>${invoices.map((i) => `<tr>
          <td><strong>${escapeHtml(i.number)}</strong></td>
          <td>${money(i.currency, i.total)}</td>
          <td class="crm-detail-sub">${escapeHtml(i.status || '')}</td></tr>`).join('')}</tbody></table></div>`
        : '<div class="crm-detail-empty">No invoices.</div>'}
    </div>`;

  const addForm = (canEdit && invoices.length) ? `
    <form data-tax-add class="crm-inbox-table" style="padding:10px; background:var(--panel-2,#f7f7f9); border-radius:8px; display:flex; flex-direction:column; gap:8px;">
      <div class="crm-detail-sub" style="font-weight:600;">Record tax withheld</div>
      <label class="field"><span class="crm-detail-sub">Invoice</span>
        <select name="invoice_id" class="input" required>${invoiceOptions}</select></label>
      <div class="row" style="gap:8px;">
        <label class="field" style="flex:1;"><span class="crm-detail-sub">Amount withheld</span>
          <input name="amount" type="number" step="0.01" min="0" class="input" required placeholder="0.00"></label>
        <label class="field" style="flex:1;"><span class="crm-detail-sub">Date</span>
          <input name="withheld_on" type="date" class="input" required></label>
      </div>
      <label class="field"><span class="crm-detail-sub">Supporting document (certificate / proof)</span>
        <input name="document" type="file" class="input" accept="image/*,application/pdf"></label>
      <label class="field"><span class="crm-detail-sub">Note (optional)</span>
        <input name="note" class="input" placeholder="e.g. 5% WHT per certificate"></label>
      <button type="submit" class="btn primary">Add record</button>
    </form>` : (canEdit ? '<div class="crm-detail-empty">This customer has no invoices to record tax against.</div>' : '');

  const right = `<div style="flex:1 1 340px; min-width:320px; display:flex; flex-direction:column; gap:12px;">
      ${addForm}
      <div>
        <div class="crm-detail-sub" style="font-weight:600; text-transform:uppercase; letter-spacing:.5px; margin-bottom:4px;">Tax withheld records</div>
        ${whs.length ? whs.map(recordRow).join('') : '<div class="crm-detail-empty">No records yet.</div>'}
      </div>
    </div>`;

  body.innerHTML = header + `<div class="row" style="gap:16px; align-items:flex-start; flex-wrap:wrap;">${invoiceList}${right}</div>`;
}

function recordRow(w) {
  const canFinance = hasRole(...FINANCE);
  const badge = `<span class="badge ${STATUS_CLASS[w.status] || ''}">${escapeHtml(w.status)}</span>`;
  const meta = [
    w.invoice_number ? `Invoice ${escapeHtml(w.invoice_number)}` : '',
    w.withheld_on ? `withheld ${escapeHtml(w.withheld_on)}` : '',
    w.created_by ? `by ${escapeHtml(w.created_by)}` : '',
  ].filter(Boolean).join(' · ');
  const trail = [
    w.approved_by ? `${w.status === 'rejected' ? 'Rejected' : 'Approved'} by ${escapeHtml(w.approved_by)}` : '',
    w.posted_by ? `Posted by ${escapeHtml(w.posted_by)}` : '',
  ].filter(Boolean).join(' · ');
  const actions = [];
  if (w.has_document) actions.push(`<button class="btn ghost" data-tax-doc="${escapeHtml(w.id)}" style="font-size:11px; padding:2px 8px;">Document</button>`);
  if (canFinance && w.status === 'pending') {
    actions.push(`<button class="btn primary" data-tax-approve="${escapeHtml(w.id)}" style="font-size:11px; padding:2px 8px;">Approve</button>`);
    actions.push(`<button class="btn ghost" data-tax-reject="${escapeHtml(w.id)}" style="font-size:11px; padding:2px 8px;">Reject</button>`);
  }
  if (canFinance && w.status === 'approved') {
    actions.push(`<button class="btn danger" data-tax-post="${escapeHtml(w.id)}" style="font-size:11px; padding:2px 8px;">Post to billing</button>`);
  }
  return `<div style="border:1px solid var(--border-soft,#e2e2e2); border-radius:8px; padding:8px 10px; margin-bottom:6px;">
    <div class="row" style="align-items:center; gap:8px;">
      <strong>${money(w.currency, w.amount)}</strong> ${badge}
      <span style="margin-left:auto; display:flex; gap:6px; flex-wrap:wrap;">${actions.join('')}</span>
    </div>
    <div class="crm-detail-sub">${escapeHtml(meta)}</div>
    ${trail ? `<div class="crm-detail-sub">${escapeHtml(trail)}</div>` : ''}
    ${w.note ? `<div class="crm-detail-sub">“${escapeHtml(w.note)}”</div>` : ''}</div>`;
}

async function onAddSubmit(ev, container) {
  const form = ev.target.closest('[data-tax-add]');
  if (!form) return;
  ev.preventDefault();
  const fd = new FormData();
  fd.append('invoice_id', form.invoice_id.value);
  fd.append('amount', form.amount.value);
  fd.append('withheld_on', form.withheld_on.value);
  if (form.note.value.trim()) fd.append('note', form.note.value.trim());
  if (form.document.files && form.document.files[0]) fd.append('document', form.document.files[0]);
  if (!form.amount.value || !form.withheld_on.value) { flash('Amount and date are required.', 'error'); return; }
  try {
    await api.fetch(API.invoicingTaxWithholdings(), { method: 'POST', body: fd });
    flash('Tax-withheld record added.', 'success');
    await loadDetail(container);
  } catch (err) {
    flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not add the record.', 'error');
  }
}

async function onBodyClick(ev, container) {
  const doc = ev.target.closest('[data-tax-doc]');
  if (doc) { window.open(API.invoicingTaxDocument(doc.dataset.taxDoc), '_blank', 'noopener'); return; }

  const approve = ev.target.closest('[data-tax-approve]');
  if (approve) { await decide(container, API.invoicingTaxApprove(approve.dataset.taxApprove), 'Approved.'); return; }

  const reject = ev.target.closest('[data-tax-reject]');
  if (reject) {
    const note = window.prompt('Reason for rejecting (optional):', '') ?? null;
    await decide(container, API.invoicingTaxReject(reject.dataset.taxReject), 'Rejected.', { note });
    return;
  }

  const post = ev.target.closest('[data-tax-post]');
  if (post) {
    if (!window.confirm('Post this withheld amount to PortaBilling as a credit?\n\nThis is NOT reversible and can only be done once. The customer balance will be resynced afterward.')) return;
    await withButtonBusy(post, async () => {
      try {
        const res = await api.fetch(API.invoicingTaxPost(post.dataset.taxPost), { method: 'POST' });
        flash(`Posted to billing. New balance: ${money(res.currency, res.balance)}`, 'success');
        await loadDetail(container);
      } catch (err) {
        const code = err && err.body && err.body.detail;
        const msg = code === 'customer_not_linked'
          ? 'This customer isn’t linked to a PortaBilling account — link them before posting.'
          : (code === 'customer_has_no_phone'
            ? 'The linked PortaBilling customer has no phone on file, which is required to post.'
            : (api.formatErrorDetail(code) || 'Could not post to billing.'));
        flash(msg, 'error');
      }
    });
  }
}

async function decide(container, url, okMsg, bodyObj) {
  try {
    await api.fetch(url, { method: 'POST', body: JSON.stringify(bodyObj || {}) });
    flash(okMsg, 'success');
    await loadDetail(container);
  } catch (err) {
    flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Action failed.', 'error');
  }
}

export default { mountTaxation };
